import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ShieldCheck, Search, MessageCircle, Check, X, RefreshCw, Hourglass, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  verified: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-300 border-red-500/30",
  expired: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

export default function AdminVerifications() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [welcomeBanner, setWelcomeBanner] = useState(null); // {wa_link, code}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/verifications?${params.toString()}`);
      setRows(data);
    } catch (err) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const approve = async (code) => {
    try {
      const { data } = await api.post(`/admin/verifications/${code}/approve`);
      toast.success(`Verified ${code}. Ab Login Credentials WhatsApp par bhejein.`);
      setWelcomeBanner({ code, wa_link: data.welcome_wa_link, message: data.welcome_message });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    try {
      await api.post(`/admin/verifications/${rejectTarget.code}/reject`, { reason: rejectReason });
      toast.success(`Rejected ${rejectTarget.code}`);
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-verifications-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">WhatsApp Verifications</h1>
        <p className="text-slate-400 mt-1">
          Shop signups verify their WhatsApp number by sending the code to support.
          Admin sees the message, then approves here to activate the 30-day trial.
        </p>
      </div>

      {welcomeBanner && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 ring-1 ring-emerald-500/20 rounded-md p-4 flex items-start gap-3" data-testid="welcome-banner">
          <Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-200">
              ✅ Shop {welcomeBanner.code} verified · Send Login Credentials
            </div>
            <div className="text-sm text-emerald-100/80 mt-1">
              Message mein Shop Name · Login Email · Password · Login URL · Support number auto-fill ho gaya hai. Neeche button dabakar WhatsApp par bhejein.
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={welcomeBanner.wa_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-3.5 py-2"
                data-testid="welcome-send-wa"
              >
                <MessageCircle className="w-4 h-4" /> Send Login Credentials on WhatsApp
                <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </a>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(welcomeBanner.message); toast.success("Message copied"); }}
                className="inline-flex items-center gap-1.5 text-emerald-200 hover:text-emerald-100 text-xs font-semibold"
                data-testid="welcome-copy-message"
              >
                <Copy className="w-3 h-3" /> Copy message
              </button>
              <button
                type="button"
                onClick={() => setWelcomeBanner(null)}
                className="text-emerald-300 hover:text-emerald-100 text-xs ml-auto"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-[#151A23] border border-slate-800 rounded-md p-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search mobile, business name, or code (HP-…)"
            className="pl-9 bg-slate-900 border-slate-700 text-slate-200"
            data-testid="verify-search"
          />
        </div>
        <div className="flex gap-1">
          {["pending", "verified", "rejected", "expired", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold capitalize border transition ${
                statusFilter === s
                  ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`verify-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <Button onClick={load} variant="outline" className="bg-slate-800 border-slate-700 text-slate-200" data-testid="verify-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-[#151A23] border border-slate-800 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Code</th>
              <th className="text-left px-4 py-3 font-semibold">Business</th>
              <th className="text-left px-4 py-3 font-semibold">Owner</th>
              <th className="text-left px-4 py-3 font-semibold">Mobile</th>
              <th className="text-left px-4 py-3 font-semibold">Signup</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                  {loading ? "Loading…" : "No verifications found."}
                </td>
              </tr>
            )}
            {rows.map((v) => (
              <tr key={v.code} className="border-t border-slate-800 hover:bg-slate-900/50" data-testid={`verify-row-${v.code}`}>
                <td className="px-4 py-3 font-mono font-bold text-amber-200 tabular">{v.code}</td>
                <td className="px-4 py-3 text-slate-200 font-medium">{v.business_name}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{v.owner_name}</td>
                <td className="px-4 py-3 text-slate-300 tabular text-xs">{v.mobile}</td>
                <td className="px-4 py-3 text-slate-500 text-xs tabular">{(v.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${STATUS_TONE[v.status] || ""}`}>
                    {v.status === "pending" && <Hourglass className="w-3 h-3" />}
                    {v.status === "verified" && <Check className="w-3 h-3" />}
                    {v.status === "rejected" && <X className="w-3 h-3" />}
                    {v.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <a
                      href={v.wa_link}
                      target="_blank"
                      rel="noreferrer"
                      title="Open user's verification message on WhatsApp"
                      className="inline-flex items-center gap-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold px-2 py-1"
                      data-testid={`verify-open-${v.code}`}
                    >
                      <MessageCircle className="w-3 h-3" /> WA
                    </a>
                    {v.status === "pending" && (
                      <>
                        <button
                          type="button"
                          onClick={() => approve(v.code)}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-2.5 py-1"
                          data-testid={`verify-approve-${v.code}`}
                          title="Verify shop & activate 30-day trial"
                        >
                          <Check className="w-3 h-3" /> Verify Shop
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectTarget(v)}
                          className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-2.5 py-1"
                          data-testid={`verify-reject-${v.code}`}
                        >
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </>
                    )}
                    {v.status === "verified" && v.welcome_wa_link && (
                      <a
                        href={v.welcome_wa_link}
                        target="_blank"
                        rel="noreferrer"
                        title="Send Login Credentials on WhatsApp"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-2.5 py-1"
                        data-testid={`verify-welcome-${v.code}`}
                      >
                        <MessageCircle className="w-3 h-3" /> Send Credentials
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reject dialog */}
      <AlertDialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectReason(""); }}}>
        <AlertDialogContent data-testid="verify-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject verification {rejectTarget?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              The user will not be able to login until they re-register. Please add a brief reason for the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (e.g., didn't receive message, suspected fake account)"
            data-testid="verify-reject-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reject} className="bg-red-600 hover:bg-red-700" data-testid="verify-reject-confirm">
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
